import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  QrCode,
  Layers,
  Settings,
  Plus,
  Edit,
  Trash2,
  ArrowLeft,
  Loader2,
  Download,
  FileSpreadsheet,
  CheckCircle,
  BarChart3,
  Palette,
  Copy,
  ClipboardList,
  Radio,
  Wallet,
  Search,
  CreditCard,
  Mail,
  MessageSquare,
  FileText,
  Info,
  Send,
  X,
  AlertTriangle
} from 'lucide-react';
import BadgeTemplateManager from '../../components/BadgeTemplateManager';

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copyingInfo, setCopyingInfo] = useState(false);
  
  // Category dialog
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ 
    category_name: '', 
    category_color: '#3B82F6', 
    display_order: 0,
    default_limits: {}
  });
  
  // Scanner limits dialog for a category
  const [scannerLimitsDialogOpen, setScannerLimitsDialogOpen] = useState(false);
  const [scannerLimitsCategory, setScannerLimitsCategory] = useState(null);
  const [scannerLimitsData, setScannerLimitsData] = useState([]);
  const [scannerLimitsEdits, setScannerLimitsEdits] = useState({});
  const [savingScannerLimits, setSavingScannerLimits] = useState(false);
  
  // Reports tab state
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportCategoryFilter, setReportCategoryFilter] = useState('all');
  const [reportScannerFilter, setReportScannerFilter] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [exportingReport, setExportingReport] = useState(false);
  const [exportingComprehensiveReport, setExportingComprehensiveReport] = useState(false);
  
  // Email delivery stats
  const [emailStats, setEmailStats] = useState(null);
  const [loadingEmailStats, setLoadingEmailStats] = useState(false);

  // Wallet tab state
  const [walletSummary, setWalletSummary] = useState(null);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [walletSearchQuery, setWalletSearchQuery] = useState('');
  const [walletSearchResults, setWalletSearchResults] = useState([]);
  const [selectedAttendeeWallet, setSelectedAttendeeWallet] = useState(null);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustData, setAdjustData] = useState({ amount: '', reason: '', adjustment_type: 'credit' });
  const [adjusting, setAdjusting] = useState(false);
  const [exportingWallet, setExportingWallet] = useState(false);
  const [settlementData, setSettlementData] = useState(null);

  // Template state
  const [templates, setTemplates] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [useCustomEmail, setUseCustomEmail] = useState(false);
  const [useCustomWhatsApp, setUseCustomWhatsApp] = useState(false);
  const [templateData, setTemplateData] = useState({
    email_subject: '',
    email_body: '',
    sender_email: '',
    sender_name: '',
    whatsapp_template_name: '',
    whatsapp_header_type: 'document',
    whatsapp_body_variables: [],
  });
  const [templateTestEmails, setTemplateTestEmails] = useState(['']);
  const [sendingTemplateTestEmail, setSendingTemplateTestEmail] = useState(false);
  const [templateTestWhatsAppNumber, setTemplateTestWhatsAppNumber] = useState('');
  const [sendingTemplateTestWhatsApp, setSendingTemplateTestWhatsApp] = useState(false);

  const BOOTH_TYPES = [
    { key: 'entry', label: 'Entry', icon: '🚪' },
    { key: 'exit', label: 'Exit', icon: '🚶' },
    { key: 'meal', label: 'Meals', icon: '🍽️' },
    { key: 'drinks', label: 'Drinks', icon: '🥤' },
    { key: 'kit', label: 'Kit Pickup', icon: '📦' },
    { key: 'gift', label: 'Gift', icon: '🎁' },
    { key: 'tshirt', label: 'T-Shirt', icon: '👕' },
    { key: 'lounge', label: 'Lounge', icon: '✨' },
    { key: 'snacks', label: 'Snacks', icon: '🍿' },
    { key: 'photo', label: 'Photo', icon: '📸' },
  ];

  const templateVariables = [
    { variable: '{{attendee_name}}', description: 'Attendee full name', key: 'attendee_name' },
    { variable: '{{ticket_id}}', description: 'Unique ticket ID', key: 'ticket_id' },
    { variable: '{{event_name}}', description: 'Event name', key: 'event_name' },
    { variable: '{{event_date}}', description: 'Event date(s)', key: 'event_date' },
    { variable: '{{event_location}}', description: 'Event venue/location', key: 'event_location' },
    { variable: '{{category_name}}', description: 'Attendee category', key: 'category_name' },
    { variable: '{{club_name}}', description: 'Club/Organization name', key: 'club_name' },
  ];

  // Edit event
  const [editMode, setEditMode] = useState(false);
  const [editedEvent, setEditedEvent] = useState({});

  const fetchEventData = useCallback(async () => {
    try {
      const [eventRes, categoriesRes, dashboardsRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/categories`),
        api.get(`/events/${eventId}/dashboards`)
      ]);
      setEvent(eventRes.data);
      setCategories(categoriesRes.data || []);
      setDashboards(dashboardsRes.data || []);
      setEditedEvent(eventRes.data);
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/admin/events');
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  const handleCopyScannerInfo = async () => {
    setCopyingInfo(true);
    try {
      // Fetch credentials from the API
      const response = await api.get(`/events/${eventId}/dashboards/credentials`);
      const { event_name, event_url_slug, dashboards: scannerCredentials } = response.data;
      
      // Build the formatted message - use event-specific scanner URL with slug
      const scannerLink = `${window.location.origin}/scanner/${event_url_slug || eventId}/login`;
      
      let message = `📋 SCANNER LOGIN DETAILS\n`;
      message += `========================\n\n`;
      message += `Event Name: ${event_name}\n`;
      message += `Scanner Link: ${scannerLink}\n\n`;
      message += `------------------------\n`;
      message += `SCANNER CREDENTIALS\n`;
      message += `------------------------\n`;
      
      if (scannerCredentials && scannerCredentials.length > 0) {
        scannerCredentials.forEach((scanner) => {
          message += `\nBooth: ${scanner.dashboard_name}\n`;
          message += `Username: ${scanner.username}\n`;
          message += `Password: ${scanner.password}\n`;
        });
      } else {
        message += `\nNo scanners configured yet.\nGo to "Manage Scanners" to create scanner accounts.\n`;
      }
      
      await navigator.clipboard.writeText(message);
      toast.success('Scanner info copied to clipboard!');
    } catch (error) {
      // Intentionally not logging the full error object to avoid leaking sensitive data
      toast.error('Failed to copy scanner info');
    } finally {
      setCopyingInfo(false);
    }
  };

  const handleUpdateEvent = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}`, {
        event_name: editedEvent.event_name,
        event_location: editedEvent.event_location,
        event_description: editedEvent.event_description,
        expected_attendees: parseInt(editedEvent.expected_attendees),
        branding_color: editedEvent.branding_color,
        status: editedEvent.status,
        wallet_enabled: editedEvent.wallet_enabled
      });
      toast.success('Event updated successfully');
      setEditMode(false);
      fetchEventData();
    } catch (error) {
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${eventId}/categories`, newCategory);
      toast.success('Category created');
      setCategoryDialogOpen(false);
      setNewCategory({ category_name: '', category_color: '#3B82F6', display_order: categories.length, default_limits: {} });
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async () => {
    try {
      await api.put(`/events/${eventId}/categories/${editingCategory.category_id}`, {
        category_name: editingCategory.category_name,
        category_color: editingCategory.category_color,
        display_order: editingCategory.display_order,
        default_limits: editingCategory.default_limits || {}
      });
      toast.success('Category updated');
      setEditingCategory(null);
      fetchEventData();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"?`)) return;
    try {
      await api.delete(`/events/${eventId}/categories/${categoryId}`);
      toast.success('Category deleted');
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Open scanner limits dialog for a category
  const openScannerLimitsDialog = async (category) => {
    setScannerLimitsCategory(category);
    setScannerLimitsDialogOpen(true);
    
    // Build scanner limits data from existing dashboards
    const limitsData = dashboards.map(d => ({
      scanner_id: d.dashboard_id,
      scanner_name: d.dashboard_name,
      scanner_type: d.dashboard_type,
      limit: d.category_limits?.[category.category_id] ?? null,
      is_unlimited: d.category_limits?.[category.category_id] === null || d.category_limits?.[category.category_id] === undefined
    }));
    
    setScannerLimitsData(limitsData);
    
    // Initialize edits state
    const edits = {};
    limitsData.forEach(s => {
      edits[s.scanner_id] = s.limit;
    });
    setScannerLimitsEdits(edits);
  };

  // Save scanner limits for a category
  const handleSaveScannerLimits = async () => {
    if (!scannerLimitsCategory) return;
    
    setSavingScannerLimits(true);
    try {
      // Build the scanner_limits payload
      const scannerLimitsPayload = {};
      Object.entries(scannerLimitsEdits).forEach(([scannerId, limit]) => {
        scannerLimitsPayload[scannerId] = limit === '' || limit === null ? null : parseInt(limit);
      });
      
      await api.post(`/events/${eventId}/categories/${scannerLimitsCategory.category_id}/bulk-update-limits`, {
        scanner_limits: scannerLimitsPayload
      });
      
      toast.success('Scanner limits updated');
      setScannerLimitsDialogOpen(false);
      fetchEventData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update scanner limits');
    } finally {
      setSavingScannerLimits(false);
    }
  };

  // Fetch email delivery stats
  const fetchEmailStats = async () => {
    setLoadingEmailStats(true);
    try {
      const response = await api.get(`/events/${eventId}/email-delivery-stats`);
      setEmailStats(response.data);
    } catch (error) {
      console.error('Failed to load email stats:', error);
    } finally {
      setLoadingEmailStats(false);
    }
  };

  // Fetch wallet summary
  const fetchWalletSummary = async () => {
    setLoadingWallet(true);
    try {
      const [summaryRes, txRes] = await Promise.all([
        api.get(`/events/${eventId}/wallet-summary`),
        api.get(`/events/${eventId}/wallet-transactions`, { params: { limit: 50 } })
      ]);
      setWalletSummary(summaryRes.data);
      setWalletTransactions(txRes.data.transactions || []);
    } catch (error) {
      // Wallet may not be enabled
    } finally {
      setLoadingWallet(false);
    }
  };

  // Search attendee for wallet operations
  const searchAttendeeWallet = async (query) => {
    if (!query || query.length < 2) {
      setWalletSearchResults([]);
      return;
    }
    try {
      const response = await api.get(`/events/${eventId}/attendees`, {
        params: { search: query, limit: 10 }
      });
      setWalletSearchResults(response.data.attendees || response.data || []);
    } catch (error) {
      setWalletSearchResults([]);
    }
  };

  // Load wallet for selected attendee
  const loadAttendeeWallet = async (attendeeId) => {
    try {
      const response = await api.get(`/attendees/${attendeeId}/wallet`);
      setSelectedAttendeeWallet(response.data);
    } catch (error) {
      toast.error('Wallet not found for this attendee');
      setSelectedAttendeeWallet(null);
    }
  };

  // Adjust wallet balance
  const handleWalletAdjust = async (e) => {
    e.preventDefault();
    if (!selectedAttendeeWallet?.wallet?.wallet_id) return;
    const adjustAmount = parseFloat(adjustData.amount);
    if (isNaN(adjustAmount) || adjustAmount <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }
    if (adjustAmount > 100000) {
      toast.error('Maximum adjustment amount is ₹1,00,000');
      return;
    }
    setAdjusting(true);
    try {
      await api.post(`/wallets/${selectedAttendeeWallet.wallet.wallet_id}/adjust`, {
        amount: adjustAmount,
        reason: adjustData.reason,
        adjustment_type: adjustData.adjustment_type
      });
      toast.success(`Wallet ${adjustData.adjustment_type}ed successfully`);
      setAdjustDialogOpen(false);
      setAdjustData({ amount: '', reason: '', adjustment_type: 'credit' });
      loadAttendeeWallet(selectedAttendeeWallet.wallet.attendee_id);
      fetchWalletSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust wallet');
    } finally {
      setAdjusting(false);
    }
  };

  const fetchSettlementData = async () => {
    try {
      const response = await api.get(`/events/${eventId}/vendor-settlements`);
      setSettlementData(response.data);
    } catch (error) {
      // Settlement data may not be available
    }
  };

  // Template functions
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await api.get(`/events/${eventId}/templates`);
      setTemplates(response.data);
      const customFlags = response.data.is_using_custom || {};
      const hasCustomEmail = customFlags.email_subject || customFlags.email_body || customFlags.sender_email || customFlags.sender_name;
      const hasCustomWhatsApp = customFlags.whatsapp_template_name || customFlags.whatsapp_header_type || customFlags.whatsapp_body_variables;
      setUseCustomEmail(!!hasCustomEmail);
      setUseCustomWhatsApp(!!hasCustomWhatsApp);
      setTemplateData({
        email_subject: response.data.custom?.email_subject || response.data.resolved?.email_subject || '',
        email_body: response.data.custom?.email_body || response.data.resolved?.email_body || '',
        sender_email: response.data.custom?.sender_email || response.data.resolved?.sender_email || '',
        sender_name: response.data.custom?.sender_name || response.data.resolved?.sender_name || '',
        whatsapp_template_name: response.data.custom?.whatsapp_template_name || response.data.resolved?.whatsapp_template_name || '',
        whatsapp_header_type: response.data.custom?.whatsapp_header_type || response.data.resolved?.whatsapp_header_type || 'document',
        whatsapp_body_variables: response.data.custom?.whatsapp_body_variables || response.data.resolved?.whatsapp_body_variables || [],
      });
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveTemplates = async () => {
    setSavingTemplates(true);
    try {
      const payload = {};
      if (useCustomEmail) {
        payload.email_subject = templateData.email_subject;
        payload.email_body = templateData.email_body;
        payload.sender_email = templateData.sender_email;
        payload.sender_name = templateData.sender_name;
      } else {
        payload.email_subject = null;
        payload.email_body = null;
        payload.sender_email = null;
        payload.sender_name = null;
      }
      if (useCustomWhatsApp) {
        payload.whatsapp_template_name = templateData.whatsapp_template_name;
        payload.whatsapp_header_type = templateData.whatsapp_header_type;
        payload.whatsapp_body_variables = templateData.whatsapp_body_variables;
      } else {
        payload.whatsapp_template_name = null;
        payload.whatsapp_header_type = null;
        payload.whatsapp_body_variables = null;
      }
      await api.put(`/events/${eventId}/templates`, payload);
      toast.success('Templates saved successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save templates');
    } finally {
      setSavingTemplates(false);
    }
  };

  const resetTemplates = async () => {
    if (!window.confirm('Reset all templates to global defaults? This will remove all custom templates for this event.')) return;
    try {
      await api.delete(`/events/${eventId}/templates`);
      toast.success('Templates reset to global defaults');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to reset templates');
    }
  };

  const insertTemplateVariable = (variable) => {
    const textarea = document.getElementById('event_email_body');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = templateData.email_body;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setTemplateData({ ...templateData, email_body: newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setTemplateData({ ...templateData, email_body: templateData.email_body + variable });
    }
    toast.success(`Inserted ${variable}`);
  };

  const addTemplateTestEmail = () => {
    setTemplateTestEmails([...templateTestEmails, '']);
  };

  const removeTemplateTestEmail = (index) => {
    if (templateTestEmails.length > 1) {
      setTemplateTestEmails(templateTestEmails.filter((_, i) => i !== index));
    }
  };

  const updateTemplateTestEmail = (index, value) => {
    const updated = [...templateTestEmails];
    updated[index] = value;
    setTemplateTestEmails(updated);
  };

  const sendTemplateTestEmail = async () => {
    const validEmails = templateTestEmails.filter(email => email.trim() && email.includes('@'));
    if (validEmails.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }
    setSendingTemplateTestEmail(true);
    try {
      const response = await api.post(`/events/${eventId}/test-email`, { emails: validEmails });
      if (response.data.success) {
        toast.success(`Test email sent to ${validEmails.length} recipient(s)!`);
      } else {
        toast.error(response.data.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTemplateTestEmail(false);
    }
  };

  const addTemplateWhatsAppVariable = () => {
    setTemplateData({
      ...templateData,
      whatsapp_body_variables: [...templateData.whatsapp_body_variables, { position: templateData.whatsapp_body_variables.length + 1, field: '' }]
    });
  };

  const updateTemplateWhatsAppVariable = (index, field) => {
    const updated = [...templateData.whatsapp_body_variables];
    updated[index] = { ...updated[index], field };
    setTemplateData({ ...templateData, whatsapp_body_variables: updated });
  };

  const removeTemplateWhatsAppVariable = (index) => {
    const updated = templateData.whatsapp_body_variables.filter((_, i) => i !== index);
    const reindexed = updated.map((v, i) => ({ ...v, position: i + 1 }));
    setTemplateData({ ...templateData, whatsapp_body_variables: reindexed });
  };

  const sendTemplateTestWhatsApp = async () => {
    if (!templateTestWhatsAppNumber || templateTestWhatsAppNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setSendingTemplateTestWhatsApp(true);
    try {
      const response = await api.post(`/events/${eventId}/test-whatsapp`, { phone_number: templateTestWhatsAppNumber });
      if (response.data.success) {
        toast.success('Test WhatsApp message sent!');
      } else {
        toast.error(response.data.error || 'Failed to send test message');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test WhatsApp');
    } finally {
      setSendingTemplateTestWhatsApp(false);
    }
  };

  const exportWalletReport = async () => {
    setExportingWallet(true);
    try {
      const response = await api.get(`/events/${eventId}/wallet-export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `wallet-report-${eventId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Wallet report downloaded');
    } catch (error) {
      toast.error('Failed to export wallet report');
    } finally {
      setExportingWallet(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  // Fetch scan summary report
  const fetchReportData = async () => {
    setLoadingReport(true);
    try {
      const params = {};
      if (reportCategoryFilter !== 'all') params.category_id = reportCategoryFilter;
      if (reportScannerFilter !== 'all') params.dashboard_id = reportScannerFilter;
      
      const response = await api.get(`/events/${eventId}/scan-summary-report`, { params });
      setReportData(response.data);
      
      // Expand all categories by default
      const expanded = {};
      response.data.categories?.forEach(cat => {
        expanded[cat.category_name] = true;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoadingReport(false);
    }
  };

  // Export report to Excel
  const handleExportReport = async () => {
    setExportingReport(true);
    try {
      const params = {};
      if (reportCategoryFilter !== 'all') params.category_id = reportCategoryFilter;
      if (reportScannerFilter !== 'all') params.dashboard_id = reportScannerFilter;
      
      const response = await api.get(`/events/${eventId}/scan-summary-report/export`, {
        params,
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'scan_report.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExportingReport(false);
    }
  };

  // Export comprehensive attendee report with all fields
  const handleExportComprehensiveReport = async () => {
    setExportingComprehensiveReport(true);
    try {
      const params = {};
      if (reportCategoryFilter !== 'all') params.category_id = reportCategoryFilter;
      
      const response = await api.get(`/events/${eventId}/attendee-report/export`, {
        params,
        responseType: 'blob'
      });
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'attendee_report.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Comprehensive report downloaded');
    } catch (error) {
      toast.error('Failed to export comprehensive report');
    } finally {
      setExportingComprehensiveReport(false);
    }
  };

  // Format scan time for display
  const formatScanTime = (isoTime) => {
    try {
      const date = new Date(isoTime);
      return date.toLocaleString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        day: '2-digit', 
        month: 'short', 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return isoTime?.slice(0, 16) || '-';
    }
  };

  // Toggle category expand/collapse
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/events')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{event?.event_name}</h1>
          <p className="text-gray-500">{event?.event_location}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{event?.actual_attendees || 0}</p>
              <p className="text-sm text-gray-500">Attendees</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Layers className="h-8 w-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold">{event?.total_categories || categories.length}</p>
              <p className="text-sm text-gray-500">Categories</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <QrCode className="h-8 w-8 text-orange-500" />
            <div>
              <p className="text-2xl font-bold">{event?.total_dashboards || 0}</p>
              <p className="text-sm text-gray-500">Scanners</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{event?.total_scans || 0}</p>
              <p className="text-sm text-gray-500">Total Scans</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Link to={`/admin/events/${eventId}/attendees`}>
          <Button variant="outline">
            <Users className="h-4 w-4 mr-2" /> Manage Attendees
          </Button>
        </Link>
        <Link to={`/admin/events/${eventId}/dashboards`}>
          <Button variant="outline">
            <QrCode className="h-4 w-4 mr-2" /> Manage Scanners
          </Button>
        </Link>
        <Link to={`/admin/events/${eventId}/scan-logs`}>
          <Button variant="outline">
            <BarChart3 className="h-4 w-4 mr-2" /> View Scan Logs
          </Button>
        </Link>
        <Link to={`/admin/events/${eventId}/analytics`}>
          <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600">
            <BarChart3 className="h-4 w-4 mr-2" /> Analytics Dashboard
          </Button>
        </Link>
        <Link to={`/admin/events/${eventId}/live-dashboard`}>
          <Button className="bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600">
            <Radio className="h-4 w-4 mr-2" /> Live Dashboard
          </Button>
        </Link>
      </div>

      {/* Scanner Portal Info Box */}
      {event?.campaign_id && (
        <Card className="mb-6 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <QrCode className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-emerald-800">Scanner Portal</p>
                  <p className="text-sm text-emerald-600">Share scanner credentials with volunteers</p>
                </div>
              </div>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleCopyScannerInfo}
                disabled={copyingInfo}
                data-testid="copy-scanner-info"
              >
                {copyingInfo ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Copying...</>
                ) : (
                  <><ClipboardList className="h-4 w-4 mr-2" /> Copy Scanner Info</>
                )}
              </Button>
            </div>
            
            {/* Info Preview */}
            <div className="mt-4 p-3 bg-white/70 rounded-lg border border-emerald-200 space-y-2 text-sm">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-emerald-600 font-medium">Event Name:</span>
                    <p className="text-emerald-800 truncate">{event.event_name}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="text-emerald-600 font-medium">Scanner Link:</span>
                    <p className="font-mono text-emerald-800 text-xs break-all">{window.location.origin}/scanner/{event.event_url_slug || eventId}/login</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/scanner/${event.event_url_slug || eventId}/login`);
                      toast.success('Scanner link copied!');
                    }}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy Link
                  </Button>
                </div>
              </div>
              <div className="pt-2 border-t border-emerald-100">
                <span className="text-emerald-600 font-medium">Scanners:</span>
                <span className="ml-2 text-emerald-800">
                  {dashboards.length > 0 
                    ? `${dashboards.length} scanner${dashboards.length > 1 ? 's' : ''} configured`
                    : 'No scanners yet - create them in "Manage Scanners"'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="badges">Badge Templates</TabsTrigger>
          <TabsTrigger value="reports" onClick={() => { 
            if (!reportData) fetchReportData();
            if (!emailStats) fetchEmailStats();
          }}>Reports</TabsTrigger>
          <TabsTrigger value="settings">Event Settings</TabsTrigger>
          <TabsTrigger value="templates" onClick={() => { if (!templates) fetchTemplates(); }}>
            <Mail className="h-4 w-4 mr-1" /> Templates
          </TabsTrigger>
          {event?.wallet_enabled && (
            <TabsTrigger value="wallet" onClick={() => { if (!walletSummary) { fetchWalletSummary(); fetchSettlementData(); } }}>
              <Wallet className="h-4 w-4 mr-1" /> Wallet
            </TabsTrigger>
          )}
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Categories</CardTitle>
              <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-category-button">
                    <Plus className="h-4 w-4 mr-2" /> Add Category
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Category</DialogTitle>
                    <DialogDescription>Create a new attendee category with default scan limits</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category Name</Label>
                      <Input
                        value={newCategory.category_name}
                        onChange={(e) => setNewCategory({...newCategory, category_name: e.target.value})}
                        placeholder="VIP, Speaker, Regular..."
                        required
                        data-testid="category-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={newCategory.category_color}
                          onChange={(e) => setNewCategory({...newCategory, category_color: e.target.value})}
                          className="w-14 h-10 p-1"
                        />
                        <Input
                          value={newCategory.category_color}
                          onChange={(e) => setNewCategory({...newCategory, category_color: e.target.value})}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2 border-t pt-4">
                      <Label className="text-base font-medium">Default Scan Limits</Label>
                      <p className="text-xs text-gray-500">Set default limits for this category. Leave empty for unlimited. Max: 9999</p>
                      <div className="grid grid-cols-2 gap-2">
                        {BOOTH_TYPES.map((booth) => (
                          <div key={booth.key} className="flex items-center gap-2">
                            <span className="text-sm w-20">{booth.icon} {booth.label}</span>
                            <Input
                              type="number"
                              min="0"
                              max="9999"
                              placeholder="∞"
                              className="w-16 h-8 text-sm"
                              value={newCategory.default_limits?.[booth.key] === 'unlimited' ? '' : (newCategory.default_limits?.[booth.key] || '')}
                              onChange={(e) => {
                                let val = e.target.value;
                                // Limit to max 9999
                                if (val !== '' && parseInt(val) > 9999) {
                                  val = '9999';
                                }
                                setNewCategory({
                                  ...newCategory,
                                  default_limits: {
                                    ...newCategory.default_limits,
                                    [booth.key]: val === '' ? null : parseInt(val)
                                  }
                                });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" data-testid="submit-category-button">Create</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {categories.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Layers className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No categories yet. Create categories to organize your attendees.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {categories.map((category) => {
                    // Count how many scanners have limits for this category
                    const scannersWithLimits = dashboards.filter(
                      d => d.category_limits?.[category.category_id] !== null && 
                           d.category_limits?.[category.category_id] !== undefined
                    );
                    const scannerLimitCount = scannersWithLimits.length;
                    
                    return (
                      <div
                        key={category.category_id}
                        className="border rounded-lg overflow-hidden"
                        data-testid={`category-${category.category_id}`}
                      >
                        {/* Main row */}
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded-full flex-shrink-0"
                              style={{ backgroundColor: category.category_color }}
                            />
                            <div>
                              <p className="font-medium">{category.category_name}</p>
                              <p className="text-sm text-gray-500">{category.attendee_count || 0} attendees</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {scannerLimitCount > 0 ? (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                                {scannerLimitCount} scanner limit{scannerLimitCount > 1 ? 's' : ''}
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                                No limits
                              </span>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openScannerLimitsDialog(category)}
                              title="Manage scanner limits for this category"
                              data-testid={`manage-limits-${category.category_id}`}
                            >
                              <Settings className="h-4 w-4 mr-1" />
                              Limits
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingCategory({...category, default_limits: category.default_limits || {}})}
                              title="Edit category"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteCategory(category.category_id, category.category_name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Scanner limits grid - show scanners with limits for this category */}
                        {scannerLimitCount > 0 && (
                          <div className="px-4 pb-4 pt-0">
                            <div className="bg-orange-50 rounded-lg p-3">
                              <p className="text-xs font-medium text-orange-700 mb-2">Scanner Scan Limits:</p>
                              <div className="flex flex-wrap gap-2">
                                {scannersWithLimits.map((scanner) => (
                                  <div 
                                    key={scanner.dashboard_id}
                                    className="flex items-center gap-1 bg-white border border-orange-200 rounded px-2 py-1 text-sm"
                                  >
                                    <span className="text-gray-600">{scanner.dashboard_name}:</span>
                                    <span className="font-semibold text-orange-600">
                                      {scanner.category_limits[category.category_id]} scans
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scanner Limits Dialog */}
          <Dialog open={scannerLimitsDialogOpen} onOpenChange={setScannerLimitsDialogOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  Scanner Limits for &ldquo;{scannerLimitsCategory?.category_name}&rdquo;
                </DialogTitle>
                <DialogDescription>
                  Set how many times attendees in this category can be scanned at each scanner.
                </DialogDescription>
              </DialogHeader>
              
              {scannerLimitsCategory && (
                <div className="space-y-4">
                  {dashboards.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <QrCode className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No scanners created yet.</p>
                      <Link to={`/admin/events/${eventId}/dashboards`}>
                        <Button variant="link" className="mt-2">Create Scanners</Button>
                      </Link>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {dashboards.map((scanner) => (
                          <div 
                            key={scanner.dashboard_id}
                            className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex-1">
                              <p className="font-medium text-sm">{scanner.dashboard_name}</p>
                              <p className="text-xs text-gray-500">{scanner.dashboard_type}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="9999"
                                placeholder="∞"
                                className="w-20 h-9 text-center"
                                value={scannerLimitsEdits[scanner.dashboard_id] ?? ''}
                                onChange={(e) => {
                                  let val = e.target.value;
                                  // Limit to max 9999
                                  if (val !== '' && parseInt(val) > 9999) {
                                    val = '9999';
                                  }
                                  setScannerLimitsEdits({
                                    ...scannerLimitsEdits,
                                    [scanner.dashboard_id]: val === '' ? null : val
                                  });
                                }}
                              />
                              <span className="text-sm text-gray-500 w-12">scans</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="bg-blue-50 p-3 rounded text-xs text-blue-700">
                        <p className="font-medium mb-1">💡 Tip:</p>
                        <p>Leave empty for unlimited scans. Limits are checked when scanning.</p>
                      </div>
                      
                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setScannerLimitsDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveScannerLimits} disabled={savingScannerLimits}>
                          {savingScannerLimits ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            'Save Limits'
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Edit Category Dialog */}
          <Dialog open={!!editingCategory} onOpenChange={() => setEditingCategory(null)}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Category</DialogTitle>
              </DialogHeader>
              {editingCategory && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category Name</Label>
                    <Input
                      value={editingCategory.category_name}
                      onChange={(e) => setEditingCategory({...editingCategory, category_name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={editingCategory.category_color}
                        onChange={(e) => setEditingCategory({...editingCategory, category_color: e.target.value})}
                        className="w-14 h-10 p-1"
                      />
                      <Input
                        value={editingCategory.category_color}
                        onChange={(e) => setEditingCategory({...editingCategory, category_color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="text-blue-700">
                      <span className="font-medium">💡 Scan Limits:</span> To set scan limits for this category, close this dialog and click the <strong>&quot;Limits&quot;</strong> button next to the category. This allows you to set limits per scanner (e.g., &quot;25th Lunch&quot;, &quot;Registration Kit&quot;).
                    </p>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setEditingCategory(null)}>Cancel</Button>
                    <Button onClick={handleUpdateCategory}>Save</Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Badge Templates Tab */}
        <TabsContent value="badges">
          <div className="space-y-4">
            {/* Blank Badges Link */}
            <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-orange-900">On-Spot Registration</h3>
                  <p className="text-sm text-orange-700">Generate blank badges for walk-in registrations</p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                  onClick={() => navigate(`/admin/events/${eventId}/blank-badges`)}
                >
                  Manage Blank Badges
                </Button>
              </CardContent>
            </Card>
            
            <BadgeTemplateManager eventId={eventId} categories={categories} />
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports">
          {/* Email Delivery Stats */}
          <Card className="mb-4">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Email Delivery Status</CardTitle>
                  <p className="text-sm text-gray-500">Track badge email delivery performance</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchEmailStats}
                  disabled={loadingEmailStats}
                >
                  {loadingEmailStats ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {emailStats ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-700">{emailStats.total_with_email}</p>
                    <p className="text-xs text-gray-500">Total with Email</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{emailStats.sent}</p>
                    <p className="text-xs text-blue-600">Sent</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{emailStats.delivered}</p>
                    <p className="text-xs text-green-600">Delivered</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">{emailStats.opened}</p>
                    <p className="text-xs text-purple-600">Opened</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-600">{emailStats.bounced}</p>
                    <p className="text-xs text-orange-600">Bounced</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{emailStats.spam}</p>
                    <p className="text-xs text-red-600">Spam</p>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-500">{emailStats.pending}</p>
                    <p className="text-xs text-gray-500">Pending</p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">Click Refresh to load email stats</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Scan Summary Report</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Live view of all registrants with their scan history</p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchReportData}
                  disabled={loadingReport}
                >
                  {loadingReport ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <><BarChart3 className="h-4 w-4 mr-1" /> Refresh</>
                  )}
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleExportReport}
                  disabled={exportingReport || !reportData}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {exportingReport ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Exporting...</>
                  ) : (
                    <><Download className="h-4 w-4 mr-2" /> Download Excel</>
                  )}
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleExportComprehensiveReport}
                  disabled={exportingComprehensiveReport}
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  title="Download report with all attendee fields and scan details"
                >
                  {exportingComprehensiveReport ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Exporting...</>
                  ) : (
                    <><Download className="h-4 w-4 mr-2" /> Full Report</>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Category:</Label>
                  <Select value={reportCategoryFilter} onValueChange={(v) => { setReportCategoryFilter(v); }}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.category_color }} />
                            {cat.category_name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Scanner:</Label>
                  <Select value={reportScannerFilter} onValueChange={(v) => { setReportScannerFilter(v); }}>
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Scanners</SelectItem>
                      {dashboards.map(d => (
                        <SelectItem key={d.dashboard_id} value={d.dashboard_id}>{d.dashboard_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={fetchReportData}>
                  Apply Filters
                </Button>
              </div>

              {/* Summary Stats */}
              {reportData && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{reportData.summary?.total_registrants || 0}</p>
                    <p className="text-xs text-blue-600">Total Registrants</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{reportData.summary?.total_checked_in || 0}</p>
                    <p className="text-xs text-green-600">Checked In</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-700">{reportData.summary?.total_scans || 0}</p>
                    <p className="text-xs text-purple-600">Total Scans</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-700">{reportData.summary?.check_in_percentage || 0}%</p>
                    <p className="text-xs text-orange-600">Check-in Rate</p>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {loadingReport && (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              )}

              {/* No Data State */}
              {!loadingReport && !reportData && (
                <div className="text-center py-12 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Click &quot;Refresh&quot; to load the report data</p>
                </div>
              )}

              {/* Report Data - Grouped by Category */}
              {!loadingReport && reportData && reportData.categories?.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No attendees found for the selected filters</p>
                </div>
              )}

              {!loadingReport && reportData && reportData.categories?.map((category) => (
                <div key={category.category_name} className="mb-4 border rounded-lg overflow-hidden">
                  {/* Category Header - Clickable to expand/collapse */}
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                    style={{ backgroundColor: `${category.category_color}15` }}
                    onClick={() => toggleCategory(category.category_name)}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: category.category_color }}
                      />
                      <span className="font-semibold">{category.category_name}</span>
                      <span className="text-sm text-gray-500">({category.attendees?.length || 0} registrants)</span>
                    </div>
                    <span className="text-gray-400">{expandedCategories[category.category_name] ? '▼' : '▶'}</span>
                  </div>

                  {/* Attendees Table - Collapsible */}
                  {expandedCategories[category.category_name] && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Ticket ID</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Phone</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-600">Total</th>
                            {reportData.scanners?.map(scanner => (
                              <th key={scanner.dashboard_id} className="px-3 py-2 text-left font-medium text-gray-600 min-w-[140px]">
                                {scanner.dashboard_name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {category.attendees?.map((attendee, idx) => (
                            <tr key={attendee.attendee_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-3 py-2 font-mono text-xs">{attendee.ticket_id}</td>
                              <td className="px-3 py-2 font-medium">{attendee.name}</td>
                              <td className="px-3 py-2 text-gray-600">{attendee.phone || '-'}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                  attendee.total_scans > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {attendee.total_scans}
                                </span>
                              </td>
                              {reportData.scanners?.map(scanner => {
                                const scanData = attendee.scanner_scans?.[scanner.dashboard_id];
                                const hasScans = scanData && scanData.scan_count > 0;
                                return (
                                  <td key={scanner.dashboard_id} className="px-3 py-2">
                                    {hasScans ? (
                                      <div className="flex items-start gap-1">
                                        <span className="text-green-600 font-bold">✓</span>
                                        <span className="text-xs text-gray-600">
                                          {scanData.scan_times?.map(t => formatScanTime(t)).join(', ')}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-300">✗</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Event Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Campaign ID Section */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-emerald-800 font-medium">Campaign ID (for Scanner Login)</Label>
                    <p className="text-sm text-emerald-600">Share this ID with your scanner operators</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-emerald-100 px-3 py-2 rounded text-lg font-mono font-bold text-emerald-800">
                      {editedEvent.campaign_id || 'Not set'}
                    </code>
                    {editMode && (
                      <Input
                        value={editedEvent.campaign_id || ''}
                        onChange={(e) => setEditedEvent({...editedEvent, campaign_id: e.target.value.toUpperCase()})}
                        placeholder="e.g., TECH2026"
                        className="w-32 uppercase"
                      />
                    )}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Name</Label>
                  <Input
                    value={editedEvent.event_name || ''}
                    onChange={(e) => setEditedEvent({...editedEvent, event_name: e.target.value})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={editedEvent.event_location || ''}
                    onChange={(e) => setEditedEvent({...editedEvent, event_location: e.target.value})}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expected Attendees</Label>
                  <Input
                    type="number"
                    min="1"
                    max="1000000"
                    value={editedEvent.expected_attendees || ''}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val !== '' && parseInt(val) > 1000000) {
                        val = '1000000';
                      }
                      setEditedEvent({...editedEvent, expected_attendees: val});
                    }}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Brand Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={editedEvent.branding_color || '#3B82F6'}
                      onChange={(e) => setEditedEvent({...editedEvent, branding_color: e.target.value})}
                      className="w-14 h-10 p-1"
                      disabled={!editMode}
                    />
                    <Input
                      value={editedEvent.branding_color || '#3B82F6'}
                      onChange={(e) => setEditedEvent({...editedEvent, branding_color: e.target.value})}
                      className="flex-1"
                      disabled={!editMode}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editedEvent.event_description || ''}
                  onChange={(e) => setEditedEvent({...editedEvent, event_description: e.target.value})}
                  disabled={!editMode}
                  rows={4}
                />
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg border bg-gray-50">
                <div className="flex items-center gap-3">
                  <Wallet className="h-5 w-5 text-gray-600" />
                  <div>
                    <Label className="font-medium">Wallet System</Label>
                    <p className="text-sm text-gray-500">Enable cashless payments for this event{!editedEvent.campaign_id && ' (requires Campaign ID)'}</p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!editedEvent.wallet_enabled}
                  disabled={!editMode}
                  onClick={() => editMode && setEditedEvent({...editedEvent, wallet_enabled: !editedEvent.wallet_enabled})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    editedEvent.wallet_enabled ? 'bg-emerald-500' : 'bg-gray-300'
                  } ${!editMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editedEvent.wallet_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              <div className="flex justify-end gap-2">
                {editMode ? (
                  <>
                    <Button variant="outline" onClick={() => { setEditMode(false); setEditedEvent(event); }}>
                      Cancel
                    </Button>
                    <Button onClick={handleUpdateEvent} disabled={saving}>
                      {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setEditMode(true)}>
                    <Edit className="h-4 w-4 mr-2" /> Edit Event
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          {loadingTemplates ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Email Template Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" /> Email Template
                    </CardTitle>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-gray-600">Use custom template</span>
                      <input
                        type="checkbox"
                        checked={useCustomEmail}
                        onChange={(e) => setUseCustomEmail(e.target.checked)}
                        className="rounded"
                      />
                    </label>
                  </div>
                  <p className="text-sm text-gray-500">
                    {useCustomEmail ? 'Custom email template for this event' : 'Using global default template from Settings'}
                  </p>
                </CardHeader>
                <CardContent>
                  {!useCustomEmail ? (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                        <Info className="h-4 w-4" />
                        <span>Preview of the global default template (read-only)</span>
                      </div>
                      <p className="text-sm"><span className="font-medium">From:</span> {templates?.resolved?.sender_name || 'Event Team'} &lt;{templates?.resolved?.sender_email || 'Not configured'}&gt;</p>
                      <p className="text-sm font-medium">Subject: {templates?.resolved?.email_subject || 'Not configured'}</p>
                      <pre className="text-sm text-gray-600 whitespace-pre-wrap mt-2 font-sans">{templates?.resolved?.email_body || 'No email body configured'}</pre>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Sender Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="event_sender_email">Sender Email</Label>
                          <Input
                            id="event_sender_email"
                            type="email"
                            value={templateData.sender_email}
                            onChange={(e) => setTemplateData({ ...templateData, sender_email: e.target.value })}
                            placeholder={templates?.resolved?.sender_email || 'noreply@yourdomain.com'}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="event_sender_name">Sender Name</Label>
                          <Input
                            id="event_sender_name"
                            value={templateData.sender_name}
                            onChange={(e) => setTemplateData({ ...templateData, sender_name: e.target.value })}
                            placeholder={templates?.resolved?.sender_name || 'Event Team'}
                          />
                        </div>
                      </div>

                      {/* Brevo Whitelisting Warning */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="text-amber-700">
                            <p className="font-medium">Sender email must be verified in Brevo</p>
                            <p className="mt-1">Before using a custom sender email, you must add and verify it in your{' '}
                              <a href="https://app.brevo.com/senders/list" target="_blank" rel="noopener noreferrer" className="text-amber-900 underline">Brevo Senders list</a>.
                              Unverified senders will cause emails to fail or land in spam. For best deliverability:
                            </p>
                            <ul className="list-disc ml-4 mt-1 space-y-0.5">
                              <li>Verify your sender domain with SPF, DKIM, and DMARC records</li>
                              <li>Use a sender email on a domain you own (not gmail.com, etc.)</li>
                              <li>Verify the sender in Brevo before sending</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="event_email_subject">Email Subject</Label>
                        <Input
                          id="event_email_subject"
                          value={templateData.email_subject}
                          onChange={(e) => setTemplateData({ ...templateData, email_subject: e.target.value })}
                          placeholder="Your Badge for {{event_name}}"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="event_email_body">Email Message</Label>
                        <Textarea
                          id="event_email_body"
                          value={templateData.email_body}
                          onChange={(e) => setTemplateData({ ...templateData, email_body: e.target.value })}
                          placeholder="Enter your email message..."
                          className="min-h-[250px] text-sm"
                        />
                      </div>

                      {/* Available Variables */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Info className="h-4 w-4" /> Available Variables
                        </Label>
                        <p className="text-xs text-gray-500">Click to insert into email body</p>
                        <div className="flex flex-wrap gap-2">
                          {templateVariables.map((item) => (
                            <button
                              key={item.variable}
                              onClick={() => insertTemplateVariable(item.variable)}
                              className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full border border-blue-200 transition-colors"
                              title={item.description}
                            >
                              <span className="font-mono">{item.variable}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Test Email */}
                      <div className="border-t pt-4 space-y-3">
                        <Label className="flex items-center gap-2">
                          <Send className="h-4 w-4" /> Send Test Email
                        </Label>
                        <div className="space-y-2">
                          {templateTestEmails.map((email, index) => (
                            <div key={index} className="flex gap-2">
                              <Input
                                type="email"
                                value={email}
                                onChange={(e) => updateTemplateTestEmail(index, e.target.value)}
                                placeholder="email@example.com"
                                className="flex-1"
                              />
                              {templateTestEmails.length > 1 && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  onClick={() => removeTemplateTestEmail(index)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addTemplateTestEmail}
                          >
                            <Plus className="h-4 w-4 mr-2" /> Add Another Email
                          </Button>
                        </div>
                        <Button
                          onClick={sendTemplateTestEmail}
                          disabled={sendingTemplateTestEmail}
                          variant="outline"
                          className="w-full"
                        >
                          {sendingTemplateTestEmail ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                          ) : (
                            <><Send className="h-4 w-4 mr-2" /> Send Test Email</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* WhatsApp Template Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" /> WhatsApp Template
                    </CardTitle>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm text-gray-600">Use custom template</span>
                      <input
                        type="checkbox"
                        checked={useCustomWhatsApp}
                        onChange={(e) => setUseCustomWhatsApp(e.target.checked)}
                        className="rounded"
                      />
                    </label>
                  </div>
                  <p className="text-sm text-gray-500">
                    {useCustomWhatsApp ? 'Custom WhatsApp template for this event' : 'Using global default template from Settings'}
                  </p>
                </CardHeader>
                <CardContent>
                  {!useCustomWhatsApp ? (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                        <Info className="h-4 w-4" />
                        <span>Preview of the global default template (read-only)</span>
                      </div>
                      <p className="text-sm"><span className="font-medium">Template Name:</span> {templates?.resolved?.whatsapp_template_name || 'Not configured'}</p>
                      <p className="text-sm"><span className="font-medium">Header Type:</span> {templates?.resolved?.whatsapp_header_type || 'document'}</p>
                      {templates?.resolved?.whatsapp_body_variables?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium">Body Variables:</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {templates.resolved.whatsapp_body_variables.map((v, i) => (
                              <span key={i} className="text-xs bg-white border rounded px-2 py-1">
                                {`{{${v.position}}}`} = {v.field}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Important Notice */}
                      <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm">
                        <div className="flex items-center gap-2 text-amber-800 font-medium mb-1">
                          <AlertTriangle className="h-4 w-4" /> WhatsApp Template Requirements
                        </div>
                        <p className="text-amber-700 text-xs">
                          Template must be pre-approved in your WhatsApp Business Account via Interakt. The template name must match exactly (case-sensitive).
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="event_whatsapp_template_name">Template Name (from Interakt)</Label>
                        <Input
                          id="event_whatsapp_template_name"
                          value={templateData.whatsapp_template_name}
                          onChange={(e) => setTemplateData({ ...templateData, whatsapp_template_name: e.target.value })}
                          placeholder="e.g., event_badge_notification"
                        />
                        <p className="text-xs text-gray-500">
                          Enter the exact template name as it appears in your Interakt dashboard (case-sensitive)
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Header Type</Label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="event_header_type"
                              value="document"
                              checked={templateData.whatsapp_header_type === 'document'}
                              onChange={(e) => setTemplateData({ ...templateData, whatsapp_header_type: e.target.value })}
                              className="text-primary"
                            />
                            <span className="text-sm">Document (PDF Badge)</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="event_header_type"
                              value="image"
                              checked={templateData.whatsapp_header_type === 'image'}
                              onChange={(e) => setTemplateData({ ...templateData, whatsapp_header_type: e.target.value })}
                              className="text-primary"
                            />
                            <span className="text-sm">Image</span>
                          </label>
                        </div>
                      </div>

                      {/* Variable Mapping */}
                      <div className="space-y-2">
                        <Label>Body Variables (in order)</Label>
                        <div className="bg-gray-50 p-3 rounded-lg text-sm mb-2">
                          <p className="text-gray-500 text-xs">
                            Map template variables in the same order as your WhatsApp template. E.g., if body is "Hello {'{{1}}'}, your badge for {'{{2}}'} is attached." then map 1 to attendee_name, 2 to event_name.
                          </p>
                        </div>
                        {templateData.whatsapp_body_variables.map((variable, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <span className="w-16 text-sm text-gray-500">{`{{${index + 1}}}`}</span>
                            <select
                              value={variable.field}
                              onChange={(e) => updateTemplateWhatsAppVariable(index, e.target.value)}
                              className="flex-1 border rounded-md px-3 py-2 text-sm"
                            >
                              <option value="">Select field...</option>
                              <option value="attendee_name">Attendee Name</option>
                              <option value="ticket_id">Ticket ID</option>
                              <option value="event_name">Event Name</option>
                              <option value="event_date">Event Date</option>
                              <option value="event_location">Event Location</option>
                              <option value="category_name">Category</option>
                              <option value="club_name">Club/Organization</option>
                            </select>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => removeTemplateWhatsAppVariable(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addTemplateWhatsAppVariable}
                        >
                          <Plus className="h-4 w-4 mr-2" /> Add Variable
                        </Button>
                      </div>

                      {/* Test WhatsApp */}
                      <div className="border-t pt-4 space-y-3">
                        <Label className="flex items-center gap-2">
                          <Send className="h-4 w-4" /> Send Test WhatsApp
                        </Label>
                        <div className="space-y-2">
                          <Input
                            type="tel"
                            value={templateTestWhatsAppNumber}
                            onChange={(e) => setTemplateTestWhatsAppNumber(e.target.value)}
                            placeholder="919876543210"
                          />
                          <p className="text-xs text-gray-500">Enter number without + or spaces (e.g., 919876543210 for India)</p>
                        </div>
                        <Button
                          onClick={sendTemplateTestWhatsApp}
                          disabled={sendingTemplateTestWhatsApp || !templateData.whatsapp_template_name}
                          variant="outline"
                          className="w-full"
                        >
                          {sendingTemplateTestWhatsApp ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
                          ) : (
                            <><MessageSquare className="h-4 w-4 mr-2" /> Send Test WhatsApp</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={resetTemplates}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Reset to Global Defaults
                </Button>
                <Button onClick={saveTemplates} disabled={savingTemplates}>
                  {savingTemplates ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    'Save Templates'
                  )}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Wallet Tab */}
        {event?.wallet_enabled && (
          <TabsContent value="wallet">
            {/* Summary Cards */}
            {walletSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-gray-500">Total Wallets</div>
                    <div className="text-2xl font-bold">{walletSummary.wallet_stats?.total_wallets || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-gray-500">Active Wallets</div>
                    <div className="text-2xl font-bold text-blue-600">{walletSummary.wallet_stats?.active_wallets || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-gray-500">Total Topped Up</div>
                    <div className="text-2xl font-bold text-green-600">{formatCurrency(walletSummary.wallet_stats?.total_topped_up)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-gray-500">Total Spent</div>
                    <div className="text-2xl font-bold text-orange-600">{formatCurrency(walletSummary.wallet_stats?.total_spent)}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-sm text-gray-500">Outstanding</div>
                    <div className="text-2xl font-bold text-purple-600">{formatCurrency(walletSummary.wallet_stats?.total_balance)}</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {walletSummary && (
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Wallet Report</h3>
                <Button
                  variant="outline"
                  onClick={exportWalletReport}
                  disabled={exportingWallet}
                >
                  {exportingWallet ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export to Excel
                </Button>
              </div>
            )}

            {loadingWallet ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Search & Manage Attendee Wallet */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Search Attendee Wallet</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search by name, email, or ticket ID..."
                        value={walletSearchQuery}
                        onChange={(e) => {
                          setWalletSearchQuery(e.target.value);
                          searchAttendeeWallet(e.target.value);
                        }}
                        className="pl-9"
                      />
                    </div>

                    {walletSearchResults.length > 0 && !selectedAttendeeWallet && (
                      <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                        {walletSearchResults.map((att) => (
                          <button
                            key={att.attendee_id}
                            onClick={() => {
                              loadAttendeeWallet(att.attendee_id);
                              setWalletSearchResults([]);
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                          >
                            <div className="font-medium">{att.name}</div>
                            <div className="text-sm text-gray-500">{att.email} | {att.ticket_id}</div>
                          </button>
                        ))}
                      </div>
                    )}

                    {selectedAttendeeWallet && (
                      <div className="space-y-4">
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold">{selectedAttendeeWallet.attendee_name}</h4>
                              <div className="text-3xl font-bold mt-2 text-green-600">
                                {formatCurrency(selectedAttendeeWallet.wallet?.balance)}
                              </div>
                              <div className="text-sm text-gray-500 mt-1">Current Balance</div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedAttendeeWallet(null);
                                setWalletSearchQuery('');
                              }}
                            >
                              Clear
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                            <div>
                              <span className="text-gray-500">Topped Up:</span>{' '}
                              <span className="font-medium">{formatCurrency(selectedAttendeeWallet.wallet?.total_topped_up)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Spent:</span>{' '}
                              <span className="font-medium">{formatCurrency(selectedAttendeeWallet.wallet?.total_spent)}</span>
                            </div>
                          </div>
                        </div>

                        <Button onClick={() => setAdjustDialogOpen(true)} className="w-full">
                          <CreditCard className="h-4 w-4 mr-2" /> Credit / Debit
                        </Button>

                        {/* Attendee transactions */}
                        {selectedAttendeeWallet.transactions?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-medium mb-2">Recent Transactions</h5>
                            <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                              {selectedAttendeeWallet.transactions.map((tx) => (
                                <div key={tx.transaction_id} className="px-4 py-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className={`font-medium ${
                                      tx.type === 'purchase' || tx.type === 'debit' ? 'text-red-600' : 'text-green-600'
                                    }`}>
                                      {tx.type === 'purchase' ? '-' : '+'}{formatCurrency(tx.amount)}
                                    </span>
                                    <span className="text-gray-400 text-xs">
                                      {new Date(tx.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                  <div className="text-gray-500 text-xs">{tx.items_description || tx.vendor_name || tx.type}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Transactions for Event */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Recent Transactions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {walletTransactions.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <CreditCard className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>No transactions yet</p>
                      </div>
                    ) : (
                      <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                        {walletTransactions.map((tx) => (
                          <div key={tx.transaction_id} className="px-4 py-3 text-sm">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className={`font-medium ${
                                  tx.type === 'purchase' ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  {tx.type === 'purchase' ? '-' : '+'}{formatCurrency(tx.amount)}
                                </span>
                                {tx.vendor_name && (
                                  <span className="text-gray-500 ml-2">at {tx.vendor_name}</span>
                                )}
                              </div>
                              <span className="text-xs text-gray-400 whitespace-nowrap">
                                {new Date(tx.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            {tx.items_description && (
                              <div className="text-gray-500 text-xs mt-1">{tx.items_description}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Vendor Settlement Report */}
                    <div className="mt-6">
                      <h5 className="text-sm font-medium mb-3">Vendor Settlement Report</h5>
                      {settlementData?.vendors?.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="grid grid-cols-3 gap-0 bg-gray-100 text-xs font-medium text-gray-600 uppercase tracking-wide">
                            <div className="px-4 py-2">Vendor Name</div>
                            <div className="px-4 py-2 text-center">Transactions</div>
                            <div className="px-4 py-2 text-right">Total Sales</div>
                          </div>
                          <div className="divide-y">
                            {settlementData.vendors.map((v) => (
                              <div key={v.vendor_id || v.vendor_name} className="grid grid-cols-3 gap-0 text-sm">
                                <div className="px-4 py-3 font-medium">{v.vendor_name || 'Unknown Vendor'}</div>
                                <div className="px-4 py-3 text-center text-gray-600">{v.transaction_count}</div>
                                <div className="px-4 py-3 text-right text-green-600 font-medium">{formatCurrency(v.total_sales)}</div>
                              </div>
                            ))}
                            <div className="grid grid-cols-3 gap-0 text-sm bg-gray-50 font-bold">
                              <div className="px-4 py-3">Grand Total</div>
                              <div className="px-4 py-3 text-center">{settlementData.vendors.reduce((sum, v) => sum + (v.transaction_count || 0), 0)}</div>
                              <div className="px-4 py-3 text-right text-green-700">{formatCurrency(settlementData.vendors.reduce((sum, v) => sum + (v.total_sales || 0), 0))}</div>
                            </div>
                          </div>
                        </div>
                      ) : walletSummary?.vendor_stats?.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="grid grid-cols-3 gap-0 bg-gray-100 text-xs font-medium text-gray-600 uppercase tracking-wide">
                            <div className="px-4 py-2">Vendor Name</div>
                            <div className="px-4 py-2 text-center">Transactions</div>
                            <div className="px-4 py-2 text-right">Total Sales</div>
                          </div>
                          <div className="divide-y">
                            {walletSummary.vendor_stats.map((v) => (
                              <div key={v._id} className="grid grid-cols-3 gap-0 text-sm">
                                <div className="px-4 py-3 font-medium">{v.vendor_name || 'Unknown Vendor'}</div>
                                <div className="px-4 py-3 text-center text-gray-600">{v.transaction_count}</div>
                                <div className="px-4 py-3 text-right text-green-600 font-medium">{formatCurrency(v.total_sales)}</div>
                              </div>
                            ))}
                            <div className="grid grid-cols-3 gap-0 text-sm bg-gray-50 font-bold">
                              <div className="px-4 py-3">Grand Total</div>
                              <div className="px-4 py-3 text-center">{walletSummary.vendor_stats.reduce((sum, v) => sum + (v.transaction_count || 0), 0)}</div>
                              <div className="px-4 py-3 text-right text-green-700">{formatCurrency(walletSummary.vendor_stats.reduce((sum, v) => sum + (v.total_sales || 0), 0))}</div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-400 text-sm">
                          <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No vendor transactions yet</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Adjust Wallet Dialog */}
            <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>Adjust Wallet Balance</DialogTitle>
                  <DialogDescription>
                    {selectedAttendeeWallet?.attendee_name} — Current: {formatCurrency(selectedAttendeeWallet?.wallet?.balance)}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleWalletAdjust} className="space-y-4">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={adjustData.adjustment_type === 'credit' ? 'default' : 'outline'}
                      onClick={() => setAdjustData({...adjustData, adjustment_type: 'credit'})}
                      className="flex-1"
                    >
                      Credit (+)
                    </Button>
                    <Button
                      type="button"
                      variant={adjustData.adjustment_type === 'debit' ? 'default' : 'outline'}
                      onClick={() => setAdjustData({...adjustData, adjustment_type: 'debit'})}
                      className="flex-1"
                    >
                      Debit (-)
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label>Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={adjustData.amount}
                      onChange={(e) => setAdjustData({...adjustData, amount: e.target.value})}
                      placeholder="500"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reason</Label>
                    <Input
                      value={adjustData.reason}
                      onChange={(e) => setAdjustData({...adjustData, reason: e.target.value})}
                      placeholder="Manual top-up at registration desk"
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={adjusting}>
                      {adjusting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {adjustData.adjustment_type === 'credit' ? 'Credit' : 'Debit'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

      </Tabs>
    </AdminLayout>
  );
};

export default EventDetail;
